'use client'

import { useEffect, useState } from 'react'
import { Bell, Clock, Check, ChefHat, Bike } from 'lucide-react'

/**
 * Animated showcase of the orders module — incoming online order ticks
 * through Neu → Angenommen → Zubereitung → Bereit → Auf Tour. Mirrors
 * the real /[restaurant]/orders day view in miniature.
 */
const STAGES = ['Neu', 'Angenommen', 'Zubereitung', 'Bereit', 'Auf Tour'] as const

const ORDER = {
  num: 1042,
  name: 'M. Schmidt',
  type: 'Lieferung',
  items: [
    { qty: 2, name: 'Margherita',  price: '8,90 €' },
    { qty: 1, name: 'Caprese',     price: '7,50 €' },
    { qty: 2, name: 'Tiramisu',    price: '5,40 €' },
  ],
  total: '34,80 €',
}

export function AnimatedOrdersFlow() {
  const [stage, setStage] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (paused) return
    const next = (stage + 1) % (STAGES.length + 1)
    const t = setTimeout(() => setStage(next % STAGES.length), 2400)
    return () => clearTimeout(t)
  }, [stage, paused])

  const stageIcon = [Bell, Check, ChefHat, Clock, Bike][stage]
  const StageIcon = stageIcon

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative rounded-2xl border border-pub-rule bg-pub-surface overflow-hidden"
      style={{ boxShadow: '0 20px 60px -20px rgba(23,106,71,0.18)' }}>

      <div className="flex items-center justify-between border-b border-pub-rule px-5 py-3 bg-pub-bg">
        <div className="flex items-center gap-2">
          <span className="flex h-2 w-2 rounded-full bg-brand-500 animate-pulse" />
          <p className="text-[12px] font-semibold text-pub-ink">Bestellungen · Live</p>
        </div>
        <p className="text-[11px] text-pub-muted">Heute · 3 offen</p>
      </div>

      <div className="p-5 space-y-4" style={{ minHeight: 380 }}>
        {/* Stage tabs */}
        <div className="flex gap-1 overflow-x-auto -mx-1 px-1">
          {STAGES.map((s, i) => (
            <div key={s}
              className={`flex-shrink-0 rounded-full px-3 py-1 text-[11px] font-semibold transition-all ${
                stage === i
                  ? 'bg-brand-600 text-white'
                  : i < stage ? 'bg-brand-100 text-brand-800' : 'bg-pub-bg text-pub-muted border border-pub-rule'
              }`}>
              {s}
            </div>
          ))}
        </div>

        {/* Card */}
        <div className="rounded-xl border-2 border-brand-600 bg-white p-4"
          style={{ animation: 'rzr-order-pulse 1.6s ease-in-out infinite' }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-pub-muted">#{ORDER.num} · {ORDER.type}</p>
              <p className="text-[15px] font-bold text-pub-ink mt-0.5">{ORDER.name}</p>
            </div>
            <div className="flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-semibold text-brand-800">
              <StageIcon className="h-3 w-3" />
              {STAGES[stage]}
            </div>
          </div>

          <ul className="space-y-1 mb-3">
            {ORDER.items.map((it) => (
              <li key={it.name} className="flex items-center justify-between text-[12px]">
                <span className="text-pub-ink">{it.qty}× {it.name}</span>
                <span className="text-pub-muted">{it.price}</span>
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between border-t border-pub-rule pt-2">
            <span className="text-[11px] text-pub-muted">Summe</span>
            <span className="text-[14px] font-bold text-pub-ink">{ORDER.total}</span>
          </div>

          {/* Action button morphs through stages */}
          <div className="mt-3 rounded-md bg-brand-600 py-2 text-center text-[12px] font-semibold text-white">
            {stage === 0 && 'Annehmen →'}
            {stage === 1 && 'In Zubereitung'}
            {stage === 2 && 'Bereit melden'}
            {stage === 3 && 'Fahrer rufen'}
            {stage === 4 && 'Tour gestartet · #LU-77'}
          </div>
        </div>

        {/* Tiny footer hint */}
        <p className="text-[10.5px] text-pub-muted text-center">
          Drucker, Küchen-Display und Fahrer-App synchron · 0 % Provision
        </p>
      </div>

      <style>{`
        @keyframes rzr-order-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(23,106,71,0.18); }
          50%      { box-shadow: 0 0 0 6px rgba(23,106,71,0); }
        }
      `}</style>
    </div>
  )
}
